import { useState, useEffect } from 'react';
import { Loader2, Save, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { invoicingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Valid Pennylane VAT rate codes. FR_000 is NOT valid; 0% = "exempt".
const VAT_RATE_OPTIONS = [
  { code: 'FR_200', label: '20% (standard)' },
  { code: 'FR_100', label: '10% (intermediate)' },
  { code: 'FR_055', label: '5.5% (reduced)' },
  { code: 'FR_021', label: '2.1% (super-reduced)' },
  { code: 'exempt', label: '0% (exempt)' },
  { code: 'not_subject_to_vat', label: 'Not subject to VAT' },
];

const VALID_CODES = new Set(VAT_RATE_OPTIONS.map((o) => o.code));

export function VatConfigPanel({ entityId }) {
  const [vatRateCode, setVatRateCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    invoicingAPI.getVatConfig(entityId)
      .then((res) => setVatRateCode(res.data.vat_rate_code || ''))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [entityId]);

  const save = async () => {
    if (!vatRateCode) {
      toast.error('Select a VAT rate code');
      return;
    }
    setSaving(true);
    try {
      await invoicingAPI.putVatConfig(entityId, vatRateCode);
      toast.success('VAT rate saved');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const isValid = vatRateCode && VALID_CODES.has(vatRateCode);
  const isStale = vatRateCode && !VALID_CODES.has(vatRateCode);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">VAT Rate Configuration</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Set the Pennylane VAT rate code for invoice line items. Required before generating invoices.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <select
            value={isStale ? '' : vatRateCode}
            onChange={(e) => setVatRateCode(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm w-64"
          >
            <option value="">-- Select VAT rate --</option>
            {VAT_RATE_OPTIONS.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </select>
          {isValid ? (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          )}
          <Button onClick={save} disabled={saving} size="sm">
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        </div>
        {isStale && (
          <p className="text-sm text-destructive mt-2">
            Current code "{vatRateCode}" is not accepted by Pennylane. Select a valid rate and save.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
